<script>
	let istVerheiratet = false;
	let einkommen = '';

	const grenzeLedig = 68000;
	const grenzeVerheiratet = 136000;
	const steuersatz = 0.42;
	const ekFaktor = 0.15;
	const sonnenErtragSatz = 0.05367;

	$: grenze = !istVerheiratet ? grenzeLedig : grenzeVerheiratet;
	$: darfBerechnen = +einkommen >= grenze;

	let mitBegrenzung = [];

	$: mitBegrenzung = darfBerechnen
		? [1, 2, 3].map((jahr) => {
				const differenz = +einkommen - grenze;
				const iabBrutto = differenz * jahr;
				const iabDeckel = 200000 * jahr;
				const iab = Math.min(iabDeckel, iabBrutto);

				const investition = iab * 2;
				const sonderAfa = (investition - iab) * 0.4;
				const steuer = (iab + sonderAfa) * steuersatz;
				const eigenkapital = investition * ekFaktor;
				const ueberschuss = steuer - eigenkapital;
				const sonne = investition * sonnenErtragSatz;
				const immobilienInvest = (ueberschuss / 20) * 100;

				return {
					jahr,
					iab,
					investition,
					sonderAfa,
					steuer,
					eigenkapital,
					ueberschuss,
					sonne,
					immobilienInvest
				};
			})
		: [];
</script>

<svelte:head>
	<title>Steuern in Vermögen mit Photovoltaik und Immobilien</title>
	<meta
		name="description"
		content="Berechne dein Vermögensaufbau-Potenzial in wenigen Sekunden mit unserem -Rechner. Einfach, schnell und kostenlos."
	/>
	<meta name="robots" content="index, follow" />
	<link rel="canonical" href="https://cashflow.michael-kreiselmeyer.de/" />

	<!-- Open Graph / Social Sharing -->
	<meta property="og:title" content="Steuern in Vermögen mit Photovoltaik und Immobilien" />
	<meta
		property="og:description"
		content="Jetzt Vermögensaufbau-Rechner ausprobieren und Potenzial ausrechnen!"
	/>
	<meta property="og:image" content="/images/social-preview.jpg" />
	<meta property="og:url" content="https://cashflow.michael-kreiselmeyer.de/" />
	<meta name="twitter:card" content="summary_large_image" />
</svelte:head>

<a
	href="#kontakt"
	class="fixed bottom-6 right-6 z-50 bg-primary-500 hover:bg-primary-400 text-white px-4 py-2 rounded-full border-secondary-300 border shadow-lg text-m font-bold transition-all animation-pulse"
>
	<svg
		class="w-6 h-6 text-secondary-900 dark:text-secondary-900 inline"
		aria-hidden="true"
		xmlns="http://www.w3.org/2000/svg"
		width="24"
		height="24"
		fill="currentColor"
		viewBox="0 0 24 24"
	>
		<path
			d="M2.038 5.61A2.01 2.01 0 0 0 2 6v12a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V6c0-.12-.01-.238-.03-.352l-.866.65-7.89 6.032a2 2 0 0 1-2.429 0L2.884 6.288l-.846-.677Z"
		/>
		<path
			d="M20.677 4.117A1.996 1.996 0 0 0 20 4H4c-.225 0-.44.037-.642.105l.758.607L12 10.742 19.9 4.7l.777-.583Z"
		/>
	</svg>
	<span class="text-secondary-900 tracking-tight inline">Jetzt anfragen</span>
</a>

<section id="top" class="bg-white dark:bg-secondary-500 antialiased">
	<div class="max-w-screen-xl px-4 py-16 mx-auto lg:px-6 sm:py-16 lg:py-24">
		<div class="flex flex-col items-center gap-8 xl:gap-16 lg:flex-row">
			<div class="text-center md:max-w-3xl lg:text-left xl:shrink-0">
				<div>
					<img
						alt="Icons die symbolisieren: Immobilien + Photovoltaik = Geld"
						class="lg:h-16 mx-auto lg:mx-0 h-12 mb-8 block dark:hidden"
						src="/images/mk-cashflow-icons-dark-v1.svg"
					/>
					<img
						alt="Icons die symbolisieren: Immobilien + Photovoltaik = Geld"
						class="lg:h-16 mx-auto lg:mx-0 sm:mx-auto h-12 mb-8 hidden dark:block"
						src="/images/mk-cashflow-icons-light-v1.svg"
					/>
					<h1
						class="font-extrabold text-4xl lg:text-7xl md:text-5xl leading-none tracking-tight text-gray-900 dark:text-white"
					>
						Steuern in Vermögen mit Photovoltaik und Immobilien.
					</h1>
					<p
						class="mt-4 text-base font-normal text-gray-500 md:max-w-3xl md:mx-auto sm:text-xl dark:text-gray-300"
					>
						Steuern clever nutzen - Vermögen nachhaltig aufbauen. - Entdecken Sie Ihr
						Steuersparpotential und buchen Sie sich über unser <a
							class="underline text-gray-600 dark:text-white font-bold"
							href="#kontakt">Kontaktformular</a
						> ihr Erstgespräch!
					</p>
				</div>

				<div class="flex items-center justify-center gap-4 mt-8 lg:justify-start">
					<a
						href="#calculator"
						class="inline-flex justify-center items-center py-3 px-5 text-base font-medium text-center dark:text-gray-950 text-gray-950 rounded-lg bg-primary-500 hover:bg-primary-400 focus:ring-4 focus:ring-primary-400 dark:focus:ring-primary-900"
					>
						Steuersparpotenzial-Rechner
					</a>

					<a
						href="#kontakt"
						class="inline-flex justify-center items-center py-3 px-5 text-base font-medium text-center text-gray-900 rounded-lg border border-gray-300 hover:bg-gray-100 focus:ring-4 focus:ring-gray-100 dark:text-white dark:border-gray-700 dark:hover:bg-white dark:focus:ring-gray-800 dark:hover:text-gray-950"
					>
						Jetzt anfragen
						<svg
							class="ml-2 -mr-1 w-5 h-5"
							fill="currentColor"
							viewBox="0 0 20 20"
							xmlns="http://www.w3.org/2000/svg"
							><path
								fill-rule="evenodd"
								d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z"
								clip-rule="evenodd"
							></path></svg
						>
					</a>
				</div>
			</div>

			<div class="max-w-md">
				<video
					controls
					class="w-xl h-auto max-w-full mx-auto rounded-lg"
					poster="/images/cashflow-video-placeholder-v1.webp"
				>
					<source src="/images/michael-kreiselmeyer-cashflow-short-v1.MOV" />
					<track kind="captions" src="" label="Keine Untertitel verfügbar" />
				</video>
			</div>
		</div>
	</div>
</section>

<section id="calculator" class="bg-primary-100 dark:bg-secondary-800 lg:py-32">
	<div class="px-4 mx-auto max-w-screen-xl text-center lg:px-12">
		<!-- Headline und Introtext -->
		<div class="py-8 px-4 mx-auto max-w-screen-xl sm:py-16 lg:py-0">
			<div class="lg:px-60 mb-8 lg:mb-16">
				<h2
					class="mb-4 lg:text-5xl md:text-3xl text-2xl tracking-tight text-center font-extrabold text-gray-900 dark:text-white"
				>
					Der Steuersparpotenzial-Rechner
				</h2>
				<p class="text-gray-500 text-center sm:text-xl dark:text-gray-400">
					Jetzt einfach und schnell ausrechnen, ob und in welcher Höhe Sie Ihre Steuerlast in
					Vermögen umwandeln können.
				</p>
			</div>

			<!-- Umschalter Familienstand -->
			<div class="mb-6 text-center">
				<label class="inline-flex items-center cursor-pointer">
					<span class="me-3 text-sm font-medium text-gray-900 dark:text-gray-300">Ledig</span>

					<!-- Toggle Switch -->
					<div class="relative">
						<input type="checkbox" bind:checked={istVerheiratet} class="sr-only peer" />
						<div
							class="w-11 h-6 bg-primary-500 rounded-full peer peer-checked:bg-secondary-300 transition-colors duration-300"
						></div>
						<div
							class="absolute top-0.5 left-0.5 bg-white w-5 h-5 rounded-full transition-transform
							duration-300 transform peer-checked:translate-x-full"
						></div>
					</div>

					<span class="ms-3 text-sm font-medium text-gray-900 dark:text-gray-300">Verheiratet</span>
				</label>
			</div>

			<!-- Zu versteuerndes Einkommmen INPUT -->
			<form class="max-w-sm mx-auto text-center">
				<label for="zve" class="block mb-2 text-sm font-bold text-gray-900 dark:text-white">
					Zu versteuerndes Einkommen (in € pro Jahr):
				</label>
				<div class="relative">
					<div class="absolute inset-y-0 start-0 flex items-center ps-3.5 pointer-events-none">
						<svg
							class="w-6 h-6 text-secondary-500 dark:text-primary-500"
							aria-hidden="true"
							xmlns="http://www.w3.org/2000/svg"
							fill="none"
							viewBox="0 0 24 24"
						>
							<path
								stroke="currentColor"
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M6 10h9.231M6 14h9.231M18 5.086A5.95 5.95 0 0 0 14.615 4c-3.738 0-6.769 3.582-6.769 8s3.031 8 6.769 8A5.94 5.94 0 0 0 18 18.916"
							/>
						</svg>
					</div>
					<input
						placeholder="z. B. 200000"
						type="number"
						id="zve"
						bind:value={einkommen}
						on:input={(e) => einkommen}
						class="bg-gray-50 font-bold border border-gray-300 text-gray-900 peer-checked:bg-primary-500 text-2xl rounded-lg focus:ring-primary-500 focus:border-primary-500 block w-full ps-10 p-4 dark:bg-secondary-900 dark:border-secondary-400 dark:placeholder-primary-400 dark:text-white dark:focus:ring-secondary-300 dark:focus:border-secondary-500"
					/>
				</div>
				<span class="mt-4 text-sm text-gray-500"
					>(Bitte verwenden Sie keine Punkte oder Kommas)</span
				>
			</form>

			{#if !einkommen || isNaN(+einkommen)}
				<div
					class="mt-12 p-4 mb-4 text-xl text-center text-blue-800 rounded-lg bg-blue-50 dark:bg-blue-500 dark:text-white"
				>
					⚠️ Geben Sie Ihr zu versteuerndes Einkommen an, damit wir Ihre Steuervorteile berechnen
					können.
				</div>
			{:else if +einkommen >= 1 && +einkommen <= 35000}
				<div
					class="mt-12 p-4 mb-4 text-xl text-center text-red-800 rounded-lg bg-red-50 dark:bg-red-500 dark:text-white"
				>
					⚠️ Bei Ihrer Einkommenshöhe ist die Nutzung eines Steuervorteils leider nicht möglich.
				</div>
			{:else if +einkommen < grenze}
				<div
					class="mt-12 p-4 mb-4 text-xl text-center text-blue-800 rounded-lg bg-blue-50 dark:bg-blue-500 dark:text-white"
				>
					⚠️ Bei Ihrer Einkommenshöhe ist eine <a href="#kontakt" class="font-bold underline"
						>persönliche Beratung</a
					> erforderlich.
				</div>
			{/if}
		</div>

		{#if +einkommen > grenze}
			<div class="flex lg:items-center flex-col w-full max-w-screen-xl">
				<svg
					class="w-24 mx-auto h-24 lg:mt-16 mb-16 lg:mb-0 text-seoncdary-500 dark:text-white"
					aria-hidden="true"
					xmlns="http://www.w3.org/2000/svg"
					width="24"
					height="24"
					fill="none"
					viewBox="0 0 24 24"
				>
					<path
						stroke="currentColor"
						stroke-linecap="round"
						stroke-linejoin="round"
						stroke-width="2"
						d="M12 19V5m0 14-4-4m4 4 4-4"
					/>
				</svg>
				<div class="flex flex-col gap-0 items-stretch mt-16 mb-16">
					<div class="flex-1 bg-primary-500 rounded-2xl rounded-b-none p-12">
						<h3 class="text-secondary-900 dark:text-scondary-900">
							<span class="text-xl">Ihre Steuererstattung:</span><br />
							<span class="lg:text-6xl md:text-6xl text-5xl">
								<strong>{mitBegrenzung[0].steuer.toLocaleString('de-DE')} €</strong>
							</span>
						</h3>
					</div>

					<div class="flex-1 bg-secondary-500 rounded-2xl rounded-t-none p-12">
						<h3 class="text-white dark:text-gray-300">
							<span class="text-xl">Vermögensaufbau aus Steuererstattung:</span><br />
							<span class="lg:text-6xl md:text-6xl text-5xl">
								<strong>
									{Math.round(
										mitBegrenzung[0].sonne * 25 + mitBegrenzung[0].immobilienInvest * 1.26973
									).toLocaleString('de-DE')} €
								</strong>
							</span>
						</h3>
					</div>
				</div>

				<a
					href="#kontakt"
					class="bg-primary-500 inline-flex justify-center mb-16 lg:mb-16 items-center py-5 px-5 text-base font-medium text-center text-l text-secondary-900 rounded-lg border border-gray-300 hover:bg-primary-400 focus:ring-4 focus:ring-gray-100 dark:text-secondary-900 dark:border-white/30 dark:hover:bg-white dark:focus:ring-gray-800 dark:hover:text-gray-950"
				>
					Jetzt anfragen
					<svg
						class="ml-2 -mr-1 w-5 h-5"
						fill="currentColor"
						viewBox="0 0 20 20"
						xmlns="http://www.w3.org/2000/svg"
						><path
							fill-rule="evenodd"
							d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z"
							clip-rule="evenodd"
						></path></svg
					>
				</a>
				<svg
					class="w-24 h-24 mx-auto lg:mb-24 mb-16 text-seoncdary-500 dark:text-white"
					aria-hidden="true"
					xmlns="http://www.w3.org/2000/svg"
					width="24"
					height="24"
					fill="none"
					viewBox="0 0 24 24"
				>
					<path
						stroke="currentColor"
						stroke-linecap="round"
						stroke-linejoin="round"
						stroke-width="2"
						d="M12 19V5m0 14-4-4m4 4 4-4"
					/>
				</svg>
			</div>
			<!-- ====================================================================================================================================-->

			<!-- GRID: 2 Cards nebeneinander -->
			<div>
				<h3
					class="mb-4 lg:text-4xl md:text-4xl text-3xl tracking-tight text-center font-extrabold text-gray-900 dark:text-white"
				>
					Im Detail:
				</h3>
			</div>
			<div
				class="flex mb-24 flex-col md:flex-col lg:flex-row items-stretch w-full h-[min-content] gap-0 mt-16 text-center"
			>
				<!-- Card 1: PV -->
				<div class="flex-2 order-1 hover:z-10">
					<div
						class="h-full p-12 rounded-lg shadow-md hover:shadow-xl bg-white dark:bg-secondary-900 border border-secondary-300 dark:border-secondary-400 transition-all lg:hover:scale-[1.02] flex flex-col items-center"
					>
						<div class="bg-secondary-50 dark:bg-secondary-600 p-4 rounded-full mb-4">
							<svg
								class="w-8 h-8 text-secondary-300 dark:text-secondary-200"
								aria-hidden="true"
								xmlns="http://www.w3.org/2000/svg"
								width="24"
								height="24"
								fill="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									fill-rule="evenodd"
									d="M13 3a1 1 0 1 0-2 0v2a1 1 0 1 0 2 0V3ZM6.343 4.929A1 1 0 0 0 4.93 6.343l1.414 1.414a1 1 0 0 0 1.414-1.414L6.343 4.929Zm12.728 1.414a1 1 0 0 0-1.414-1.414l-1.414 1.414a1 1 0 0 0 1.414 1.414l1.414-1.414ZM12 7a5 5 0 1 0 0 10 5 5 0 0 0 0-10Zm-9 4a1 1 0 1 0 0 2h2a1 1 0 1 0 0-2H3Zm16 0a1 1 0 1 0 0 2h2a1 1 0 1 0 0-2h-2ZM7.757 17.657a1 1 0 1 0-1.414-1.414l-1.414 1.414a1 1 0 1 0 1.414 1.414l1.414-1.414Zm9.9-1.414a1 1 0 0 0-1.414 1.414l1.414 1.414a1 1 0 0 0 1.414-1.414l-1.414-1.414ZM13 19a1 1 0 1 0-2 0v2a1 1 0 1 0 2 0v-2Z"
									clip-rule="evenodd"
								/>
							</svg>
						</div>
						<h3 class="text-xl font-bold text-secondary-500 dark:text-secondary-200 mb-2">
							Photovoltaik-Investment
						</h3>
						<p class="text-gray-700 dark:text-gray-300">
							Investition: <strong>{mitBegrenzung[0].investition.toLocaleString('de-DE')} €</strong
							><br />
							<!-- Steuererstattung:
							<strong>{mitBegrenzung[0].steuer.toLocaleString('de-DE')} €</strong><br /> -->
							Generiertes Eigenkapital:
							<strong>{mitBegrenzung[0].eigenkapital.toLocaleString('de-DE')} €</strong>
						</p>
					</div>
				</div>

				<!-- Card 3: Vermögensaufbau -->
				<div class="flex-3 lg:order-2 order-3">
					<div
						class=" p-12 rounded-lg shadow-lg bg-secondary-500 dark:bg-secondary-500 border border-secondary-100 dark:border-secondary-300 transition-all lg:scale-[1.05] lg:hover:scale-[1.1] flex flex-col items-center hover:shadow-2xl"
					>
						<div class="bg-secondary-400 dark:bg-secondary-500/20 p-4 rounded-full mb-4">
							<svg
								class="w-10 h-10 text-primary-500 dark:text-primary-300"
								aria-hidden="true"
								xmlns="http://www.w3.org/2000/svg"
								width="24"
								height="24"
								fill="none"
								viewBox="0 0 24 24"
							>
								<path
									stroke="currentColor"
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M4 4.5V19a1 1 0 0 0 1 1h15M7 14l4-4 4 4 5-5m0 0h-3.207M20 9v3.207"
								/>
							</svg>
						</div>
						<h3 class="text-2xl font-bold text-white dark:text-white mb-2">
							Vermögensaufbau in 25 Jahren
						</h3>
						<p class="text-white/80 dark:text-gray-300 leading-relaxed">
							PV-Ertrag pro Jahr: <strong>{mitBegrenzung[0].sonne.toLocaleString('de-DE')} €</strong
							><br />
							PV-Ertrag (25 Jahre):
							<strong>{(mitBegrenzung[0].sonne * 25).toLocaleString('de-DE')} €</strong><br />
							Immobilienwert (mit 1% Wertsteigerung gerechnet):
							<strong
								>{(mitBegrenzung[0].immobilienInvest * 1.26973).toLocaleString('de-DE')} €</strong
							><br />
							<br />
							<span class="font-semibold text-2xl text-primary-500 dark:text-primary-300">
								→ Gesamtpotenzial: <strong
									>{Math.round(
										mitBegrenzung[0].sonne * 25 + mitBegrenzung[0].immobilienInvest * 1.26973
									).toLocaleString('de-DE')} €</strong
								>
							</span>
						</p>
					</div>
				</div>

				<!-- Card 2: Immobilien -->
				<div class="flex-2 order-2 lg:order-3">
					<div
						class="h-full p-12 align-middle rounded-lg shadow-md hover:shadow-xl bg-white dark:bg-secondary-900 border border-secondary-300 dark:border-secondary-400 transition-all lg:hover:scale-[1.02] flex flex-col items-center"
					>
						<div class="bg-secondary-50 dark:bg-secondary-700 p-4 rounded-full mb-4">
							<svg
								class="w-8 h-8 text-secondary-300 dark:text-secondary-200"
								aria-hidden="true"
								xmlns="http://www.w3.org/2000/svg"
								width="24"
								height="24"
								fill="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									fill-rule="evenodd"
									d="M11.293 3.293a1 1 0 0 1 1.414 0l6 6 2 2a1 1 0 0 1-1.414 1.414L19 12.414V19a2 2 0 0 1-2 2h-3a1 1 0 0 1-1-1v-3h-2v3a1 1 0 0 1-1 1H7a2 2 0 0 1-2-2v-6.586l-.293.293a1 1 0 0 1-1.414-1.414l2-2 6-6Z"
									clip-rule="evenodd"
								/>
							</svg>
						</div>
						<h3 class="text-xl font-bold text-secondary-500 dark:text-secondary-200 mb-2">
							Immobilien-Investment
						</h3>
						<p class="text-gray-700 dark:text-gray-300">
							Investition (20 %):
							<strong>{mitBegrenzung[0].immobilienInvest.toLocaleString('de-DE')} €</strong>
							<br />
							Eigenkapital aus Steuererstattung:
							<strong>{mitBegrenzung[0].ueberschuss.toLocaleString('de-DE')} €</strong><br />
						</p>
					</div>
				</div>
			</div>

			<a
				href="#kontakt"
				class="inline-flex justify-center mb-16 lg:mb-0 items-center py-5 px-5 text-base font-medium text-center text-l text-gray-900 rounded-lg border border-gray-300 hover:bg-gray-100 focus:ring-4 focus:ring-gray-100 dark:text-white dark:border-white/30 dark:hover:bg-white dark:focus:ring-gray-800 dark:hover:text-gray-950"
			>
				Jetzt anfragen
				<svg
					class="ml-2 -mr-1 w-5 h-5"
					fill="currentColor"
					viewBox="0 0 20 20"
					xmlns="http://www.w3.org/2000/svg"
					><path
						fill-rule="evenodd"
						d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z"
						clip-rule="evenodd"
					></path></svg
				>
			</a>
		{/if}
	</div>
</section>

<section class="bg-primary-100 dark:bg-secondary-800">
	<div class="pb-8 px-4 mx-auto max-w-screen-xl lg:pb-16 grid">
		<div>
			<iframe
				class="mx-auto w-full lg:max-w-2xl h-64 rounded-lg sm:h-96 shadow-xl"
				src="https://www.youtube.com/embed/DRjgIryFc7o?si=qDV9ejsTD5GkfaUB"
				title="YouTube video player"
				frameborder="0"
				allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
				allowfullscreen
			></iframe>
		</div>
	</div>
</section>

<section id="testimonial" class="bg-secondary-500 dark:bg-secondary-900 py-10">
	<div class="max-w-screen-xl px-4 py-8 mx-auto text-center lg:py-16 lg:px-6">
		<figure class="max-w-screen-md mx-auto">
			<svg
				class="h-12 mx-auto mb-3 text-white dark:text-gray-600"
				viewBox="0 0 24 27"
				fill="none"
				xmlns="http://www.w3.org/2000/svg"
			>
				<path
					d="M14.017 18L14.017 10.609C14.017 4.905 17.748 1.039 23 0L23.995 2.151C21.563 3.068 20 5.789 20 8H24V18H14.017ZM0 18V10.609C0 4.905 3.748 1.038 9 0L9.996 2.151C7.563 3.068 6 5.789 6 8H9.983L9.983 18L0 18Z"
					fill="currentColor"
				/>
			</svg>
			<blockquote>
				<p class="text-2xl lg:text-4xl font-extrabold text-white dark:text-white">
					"Sehr fundierte Beratung, ehrlich, authentisch und seriös. Ich bin so überzeugt, dass ich
					bereits zum wiederholten Male investiere!."
				</p>
			</blockquote>
			<figcaption class="flex items-center justify-center mt-6 space-x-3">
				<div class="flex items-center divide-x-2 divide-gray-500 dark:divide-gray-700">
					<div class="pr-3 font-medium text-white dark:text-white">Roman Suchy</div>
					<div class="pl-3 text-sm font-light text-white dark:text-gray-400">Unternehmer</div>
				</div>
			</figcaption>
		</figure>
	</div>
</section>

<section id="about" class="bg-white dark:bg-secondary-800">
	<div class="py-8 px-4 mx-auto max-w-screen-xl lg:py-32 lg:px-6">
		<h2
			class="mb-4 lg:text-5xl md:text-3xl text-2xl tracking-tight text-center font-extrabold text-gray-900 dark:text-white"
		>
			Was wir tun
		</h2>
		<div class="grid gap-6 mt-12 lg:mt-14 lg:gap-12 md:grid-cols-3">
			<div class="flex mb-2 md:flex-col md:mb-0">
				<img
					class="mr-4 w-auto h-36 md:w-full md:h-auto rounded-lg"
					src="/images/fkd-steuern-reduzieren-vertical-v1.webp"
					alt="Lächelnder Unternehmer im Anzug um den Geldscheine herumfliegen"
				/>
				<div>
					<h3 class="text-2xl font-bold md:mt-4 mb-2.5 text-gray-900 dark:text-white">
						Steuerlast reduzieren
					</h3>
					<p class="text-gray-500 dark:text-gray-400">
						Als Topverdiener die steuerliche Effizienz Ihrer Investitionen maximieren.
					</p>
				</div>
			</div>
			<div class="flex mb-2 md:flex-col md:mb-0">
				<img
					class="mr-4 w-auto h-36 md:w-full md:h-auto rounded-lg"
					src="/images/intro-ekb-background-solarpanel.webp"
					alt="Eine Photovoltaikanlage. Im Hintergrund scheint die Sonne."
				/>
				<div>
					<h3 class="text-2xl font-bold md:mt-4 mb-2.5 text-gray-900 dark:text-white">
						Photovoltaik Investments
					</h3>
					<p class="text-gray-500 dark:text-gray-400">
						Nicht nur nachhaltig investieren, sondern investieren in Nachhaltigkeit.
					</p>
				</div>
			</div>
			<div class="flex md:flex-col">
				<img
					class="mr-4 w-auto h-36 md:w-full md:h-auto rounded-lg"
					src="/images/intro-hgi-background-haeuserfront-v1.webp"
					alt="Bild eines mehrstöckigen Mehrfamilien-Gebäudes"
				/>
				<div>
					<h3 class="text-2xl font-bold md:mt-4 mb-2.5 text-gray-900 dark:text-white">
						Immobilien Investments
					</h3>
					<p class="text-gray-500 dark:text-gray-400">
						Immobilien sind nach wie vor eine der Top-Investitionen für die Zukunft.
					</p>
				</div>
			</div>
		</div>
	</div>
</section>

<section id="unternehmensgruppe" class="bg-white dark:bg-secondary-600 pb-9">
	<div class="py-16 px-4 mx-auto max-w-screen-xl text-center lg:py-16 lg:px-20">
		<h3
			class="mb-4 text-2xl font-extrabold tracking-tight leading-none text-gray-900 md:text-2xl lg:text-3xl dark:text-white"
		>
			Die Unternehmensgruppe
		</h3>

		<div class="flex flex-col md:flex-row items-center justify-between gap-8 mt-8">
			<a href="https://www.finanzkonzepte-deutschland.de/" target="_blank" class="p-4">
				<img
					alt="Logo von Finanzkonzepte-Deutschland in Dunkel"
					class="h-16 hover:brightness-150 dark:hidden block mb-8 md:mb-0"
					src="/images/fkd-logo-dark.svg"
				/>
				<img
					alt="Logo von Finanzkonzepte-Deutschland in Hell"
					class="h-16 hover:brightness-150 dark:block hidden"
					src="/images/fkd-light-logo.svg"
				/>
			</a>

			<a href="https://hg-immoservice-sued.de/" target="_blank" class="p-4">
				<img
					alt="Logo von FKD Immo in Dunkel"
					class="h-16 hover:brightness-150 dark:hidden block mb-8 md:mb-0"
					src="/images/fkdimmo-logo-dark1.svg"
				/>
				<img
					alt="Logo von FKD Immo in Hell"
					class="h-16 hover:brightness-150 dark:block hidden"
					src="/images/fkdimmo-logo-light1.svg"
				/>
			</a>

			<a href="https://energiekonzepte-bayern.de/" target="_blank" class="p-4">
				<img
					alt="Logo von Energiekonzepte-Bayern in Dunkel"
					class="h-16 hover:brightness-150 dark:hidden block mb-8 md:mb-0"
					src="/images/ekb-dark-logo.svg"
				/>
				<img
					alt="Logo von Energiekonzepte-Bayern in Hell"
					class="h-16 hover:brightness-150 dark:block hidden"
					src="/images/ekb-light-logo.svg"
				/>
			</a>
		</div>
	</div>
</section>

<section id="kontakt" class="py-10 bg-primary-200 dark:bg-secondary-800 lg:py-32">
	<h2
		class="mb-4 lg:text-5xl md:text-3xl text-2xl tracking-tight text-center font-extrabold text-gray-900 dark:text-white"
	>
		Kontakt
	</h2>
	<script data-b24-form="inline/115/16ldjp" data-skip-moving="true">
		(function (w, d, u) {
			var s = d.createElement('script');
			s.async = true;
			s.src = u + '?' + ((Date.now() / 180000) | 0);
			var h = d.getElementsByTagName('script')[0];
			h.parentNode.insertBefore(s, h);
		})(window, document, 'https://cdn.bitrix24.de/b18970335/crm/form/loader_115.js');
	</script>
</section>
